import { useContext } from "react";
import CompanyService, { DataType } from "../../services/CompanyService";
import { FilterCollection } from "../../services/FilterService";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../shared/components/AdvancedFilters";
import Table, { TableHeader, TableHeaderWithFieldId } from "../shared/Table";
import { useHistory, useLocation } from "react-router-dom";
import TableContext from "../task/TableContext";
import ClientService from "../../services/ClientService";
import { TranslationService } from "../../services/TranslationService";
import ReportCustomerAnalysisProvider, {
  CustomerAnalysisTableContextValues
} from "./providers/ReportCustomerAnalysisProvider";
import { CustomerAnalysisListEntities } from "./entities/CustomerAnalysisListResponse";
import {
  formatAddDate,
  formatCurrency,
  formatInteger,
  formatNumber
} from "../../utils/FormatUtils";
import { Cast } from "../../utils/Utils";
import { CustomerAnalysisListRequest } from "./entities/CustomerAnalysisListRequest";
import { percentFormatCollection } from "../../utils/PercentCollection";
import { ButtonTooltipIcon } from "../shared/components/ButtonTooltipIcon";
import "../client/invoice/invoiceStyles.css";

const ReportCustomerAnalysis = () => {
  return (
    <ReportCustomerAnalysisProvider>
      <ReportCustomerAnalysisContainer />
    </ReportCustomerAnalysisProvider>
  );
};

const ReportCustomerAnalysisContainer = () => {
  const { search } = useLocation<
    { prevRequest?: CustomerAnalysisListRequest } | undefined
  >();
  const tableHeaders = getTableHeaders();
  const urlParams = new URLSearchParams(search);
  const filterQS = urlParams.get("filter")
    ? [...urlParams.entries()]
        .map(
          (x, i) => "filter" + i.toString() + "=" + x[1].replace("filter0=", "")
        )
        .join("&")
    : "";
  const { request, response } = useContext(
    TableContext
  ) as CustomerAnalysisTableContextValues;

  const exportData = async () => {
    await ClientService.exportAnalysisReport({
      ...request,
      expectedRows: response?.itemCount || 1
    });
  };

  return (
    <div className="container-fluid padding">
      <div className="d-flex justify-content-between">
        <h2>{TranslationService.translate.CustomerAnalysis}</h2>
      </div>
      <div className="mh-100 p-0">
        <div className="genericHeader">
          <div className="searcherFilterHeader actionButtonsHeader">
            {CompanyService.canDo("export") && (
              <ButtonTooltipIcon
                title={TranslationService.translate.Export}
                icon="fa-light fa-arrow-down-to-line"
                onClick={exportData}
              />
            )}
            <AdvancedFiltersButton savedFiltersId={TYPE_REPORT["Person"]} />
          </div>
        </div>
        <div className="pt-0">
          <AdvancedFilters
            page={FilterCollection.ReportCustomerAnalysis}
            defaultValue={filterQS}
          />
        </div>
        <Table
          headers={tableHeaders}
          item={ReportCustomerAnalysisItem}
          stickyHeader={true}
          fillHeight={true}
        />
      </div>
    </div>
  );
};

const ReportCustomerAnalysisItem = ({ data }: { data: any }) => {
  const { currentLanguage } = TranslationService;
  const tableHeaders = getTableHeaders();
  const history = useHistory();

  const handleNavigate = () =>
    history.push(`/${currentLanguage}/client/details?id=${data.PersonId}`);

  return (
    <tr
      key={data.PersonId}
      style={{ cursor: "pointer" }}
      onClick={handleNavigate}
    >
      {tableHeaders.map((header) => (
        <ClientColumn key={header.fieldId} data={data} header={header} />
      ))}
    </tr>
  );
};

const ClientColumn = ({
  data,
  header
}: {
  data: CustomerAnalysisListEntities.ListItem;
  header: TableHeaderWithFieldId;
}) => {
  const getValue = () => {
    if (header.key.startsWith("ioadditional.")) {
      return (
        data.additionals.find((x) => x.Id.toString() === header.fieldId)
          ?.Value ??
        Cast<{ transactionadditionals?: typeof data.additionals }>(
          data
        ).transactionadditionals?.find(
          (x) => x.Id.toString() === header.fieldId
        )?.Value
      );
    }
    return data[header.fieldId as keyof CustomerAnalysisListEntities.ListItem];
  };

  const negativeValueClass = CompanyService.getSetting("iosnegativeamountinred")
    ? "text-danger"
    : "";
  const value = getValue();

  switch (header.fieldId) {
    case "Name":
      return <td className="th-truncate">{data.Name}</td>;
    case "dueDays":
    case "averageAgingDays":
    case "averagePaymentDays":
    case "count":
      return <td className="text-end th-truncate">{formatInteger(value)}</td>;
    case "percentDue":
      return (
        <td className="text-end th-truncate">
          {percentFormatCollection(data.overdueAmount, data.amount)}
        </td>
      );
  }

  if (value === undefined || value === null || value === "") {
    return <td></td>;
  }

  switch (header.type) {
    case DataType.Currency:
      return (
        <td
          className={
            "text-end th-truncate " +
            (parseFloat(value) < 0 ? negativeValueClass : "")
          }
        >
          {formatCurrency(
            parseFloat(value),
            CompanyService.getDefaultCurrencyId(),
            formatNumber
          )}
        </td>
      );
    case DataType.Number:
      return <td className="text-end th-truncate">{formatInteger(value)}</td>;
    case DataType.Date:
      return <td className="th-truncate">{formatAddDate(value)}</td>;
    case DataType.List: {
      const additionalId = data.additionals.find(
        (x) => x.Id.toString() === header.fieldId
      )?.Id;
      const additionalDefinition =
        CompanyService.getAdditionalDefinitions().find(
          (x) =>
            x.AdditionalDefinitionID.toString() === additionalId?.toString()
        );
      const definition = additionalDefinition?.AdditionalDefinitionItems.find(
        (x) => x.AdditionalDefinitionItemID.toString() === value
      );
      return <td className="th-truncate">{definition?.Value}</td>;
    }
    default:
      return <td className="th-truncate">{value}</td>;
  }
};

function getTableHeaders() {
  const tableHeaders = [
    {
      ...new TableHeader(
        "amount",
        TranslationService.translate.ReportAmount,
        true,
        true,
        "w-200px th-truncate"
      ),
      fieldId: "amount",
      type: DataType.Currency
    },
    {
      ...new TableHeader(
        "overdueamount",
        TranslationService.translate.ReportOverdueAmount,
        true,
        true,
        "w-200px th-truncate"
      ),
      fieldId: "overdueAmount",
      type: DataType.Currency
    },
    {
      ...new TableHeader(
        "percentDue",
        TranslationService.translate.ReportPercentDue,
        true,
        false,
        "w-180px th-truncate"
      ),
      fieldId: "percentDue",
      type: DataType.Percentage
    },
    {
      ...new TableHeader(
        "count",
        TranslationService.translate.ReportPendingInvoices,
        true,
        true,
        "w-250px th-truncate"
      ),
      fieldId: "count",
      type: DataType.Number
    },
    {
      ...new TableHeader(
        "averagePaymentDays",
        TranslationService.translate.ReportAveragePaymentDays,
        true,
        true,
        "w-250px th-truncate"
      ),
      fieldId: "averagePaymentDays",
      type: DataType.Number
    },
    {
      ...new TableHeader(
        "averageAgingDays",
        TranslationService.translate.ReportAverageAgingDays,
        true,
        true,
        "w-250px th-truncate"
      ),
      fieldId: "averageAgingDays",
      type: DataType.Number
    },
    {
      ...new TableHeader(
        "dueDays",
        TranslationService.translate.ReportDueDays,
        true,
        true,
        "w-200px th-truncate"
      ),
      fieldId: "dueDays",
      type: DataType.Number
    }
  ];

  // const ClientHeaders = ClientService.getCustomerAnalysisHeaders();
  const ClientAdditionalHeaders = ClientService.getClientHeaders();

  // Agrandar el ancho de las columnas de clientes y agregar truncamiento
  ClientAdditionalHeaders.forEach((header) => {
    if (header.fieldId === "Name") {
      header.className = "long-text w-300px th-truncate";
    } else if (!header.className || header.className.trim() === "") {
      header.className = "w-200px th-truncate";
    } else {
      // Agregar ancho y truncamiento si no tiene uno ya definido
      if (!header.className.includes("w-")) {
        header.className = header.className.trim() + " w-200px";
      }
      if (!header.className.includes("th-truncate")) {
        header.className = header.className.trim() + " th-truncate";
      }
    }
  });

  return [...ClientAdditionalHeaders, ...tableHeaders];
}

export default ReportCustomerAnalysis;
